import { useEffect, useState } from "react";
import { Loader2, Save, ClipboardCheck, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SKIP_STEPS = [
  ["check_in", "Vehicle Check-In"],
  ["awaiting_parts", "Awaiting Parts"],
  ["quality_check", "Quality Check / Final Inspection"],
  ["customer_notified", "Customer Notified"],
  ["ready_for_collection", "Ready for Collection"],
];

/** Garage config: final-inspection checklist + which optional steps can be skipped. */
export function WorkflowSettings() {
  const [insp, setInsp] = useState({ enabled: false, mandatory: false, items: [] });
  const [skip, setSkip] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api("/garage/settings").then((g) => {
      setInsp({ enabled: false, mandatory: false, items: [], ...(g.inspection_checklist || {}) });
      setSkip(g.skippable_steps || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api("/garage/settings", { method: "PATCH", body: JSON.stringify({ inspection_checklist: insp, skippable_steps: skip }) });
      toast.success("Workflow settings saved");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-6" data-testid="workflow-settings">
      <h3 className="mb-4 flex items-center gap-2 text-base font-extrabold uppercase tracking-wide text-midnight"><ClipboardCheck className="h-5 w-5 text-brand" />Workflow & Final Inspection</h3>

      <label className="flex items-center gap-2"><input type="checkbox" checked={insp.enabled} onChange={(e) => setInsp({ ...insp, enabled: e.target.checked })} data-testid="insp-enabled" className="h-5 w-5 rounded border-input" /><span className="text-sm font-bold text-midnight">Require a final inspection checklist before Ready for Collection</span></label>
      {insp.enabled && (
        <div className="ml-7 mt-2 space-y-2">
          <label className="flex items-center gap-2"><input type="checkbox" checked={insp.mandatory} onChange={(e) => setInsp({ ...insp, mandatory: e.target.checked })} data-testid="insp-mandatory" className="h-5 w-5 rounded border-input" /><span className="text-sm text-midnight">Mandatory — cannot be skipped by override</span></label>
          <div className="space-y-1.5">
            {(insp.items || []).map((it, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`insp-item-row-${i}`}>
                <input value={it} onChange={(e) => setInsp({ ...insp, items: insp.items.map((x, j) => j === i ? e.target.value : x) })} className="flex-1 rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:border-brand" />
                <button onClick={() => setInsp({ ...insp, items: insp.items.filter((_, j) => j !== i) })} className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-secondary"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={() => setInsp({ ...insp, items: [...(insp.items || []), ""] })} data-testid="insp-add-item" className="inline-flex items-center gap-1 text-sm font-bold text-brand-2"><Plus className="h-4 w-4" />Add check item</button>
          </div>
        </div>
      )}

      <p className="mb-2 mt-5 text-sm font-extrabold uppercase tracking-wide text-midnight">Steps staff may Skip / Override</p>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {SKIP_STEPS.map(([key, label]) => {
          const allowed = skip[key] !== false;
          const lockedByMandatory = key === "quality_check" && insp.enabled && insp.mandatory;
          return (
            <label key={key} className="flex items-center gap-2" data-testid={`skip-cfg-${key}`}>
              <input type="checkbox" checked={allowed && !lockedByMandatory} disabled={lockedByMandatory} onChange={(e) => setSkip({ ...skip, [key]: e.target.checked })} className="h-5 w-5 rounded border-input" />
              <span className={`text-sm ${lockedByMandatory ? "text-muted-foreground line-through" : "text-midnight"}`}>{label}</span>
            </label>
          );
        })}
      </div>

      <button onClick={save} disabled={saving} data-testid="workflow-settings-save" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save workflow settings
      </button>
    </div>
  );
}
